import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Search, LogOut, Package, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Navbar({ theme, settings }) {
  const [menuOpen, setMenuOpen]       = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const { cartCount } = useCart();
  const { user, signOut } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor  = theme?.accentColor  || "#c9a84c";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setUserDropOpen(false); }, [location]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    setUserDropOpen(false);
  };

  const storeName    = settings?.storeName    || "ACHU Health Mix";
  const storeTagline = settings?.storeTagline || "We Care for Your Health";
  const logoUrl      = settings?.logoUrl      || "";

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? `${primaryColor}f5` : primaryColor,
        backdropFilter: scrolled ? "blur(12px)" : "none",
        boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.3)" : "none",
        transition: "all 0.3s ease",
        height: 64,
        display: "flex", alignItems: "center",
        padding: "0 16px",
        justifyContent: "space-between",
        gap: 8,
      }}>

        {/* ── Logo ── */}
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", flexShrink: 0, minWidth: 0, flex: 1 }}>
          {logoUrl
            ? <img src={logoUrl} alt={storeName} style={{ height: 40, width: "auto", maxWidth: 44, objectFit: "contain", flexShrink: 0, borderRadius: 4 }} />
            : (
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${accentColor}`, flexShrink: 0 }}>
                <span style={{ color: accentColor, fontWeight: 900, fontSize: "0.9rem" }}>A</span>
              </div>
            )
          }
          {/* Name + tagline — hidden on very small screens */}
          <div style={{ minWidth: 0, overflow: "hidden" }} className="nav-brand-text">
            <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.88rem", lineHeight: 1.2, fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {storeName}
            </div>
            <div style={{ color: accentColor, fontSize: "0.55rem", letterSpacing: 1.5, whiteSpace: "nowrap", textTransform: "uppercase" }}>
              {storeTagline}
            </div>
          </div>
        </Link>

        {/* ── Desktop nav links ── */}
        <div style={{ display: "flex", gap: 24, alignItems: "center", flexShrink: 0 }} className="desktop-nav">
          {[["Home", "/"], ["Shop", "/shop"], ["About", "/about"], ["Contact", "/contact"]].map(([label, path]) => (
            <Link key={path} to={path} style={{
              color: location.pathname === path ? accentColor : "rgba(255,255,255,0.85)",
              textDecoration: "none", fontSize: "0.82rem",
              letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 700,
              borderBottom: location.pathname === path ? `2px solid ${accentColor}` : "2px solid transparent",
              paddingBottom: 2, whiteSpace: "nowrap",
            }}>{label}</Link>
          ))}
        </div>

        {/* ── Right icons ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>

          {/* Search — hidden on mobile */}
          <Link to="/search" style={{ color: "rgba(255,255,255,0.8)", textDecoration: "none" }} className="desktop-only" title="Search">
            <Search size={19} />
          </Link>

          {/* Cart */}
          <Link to="/cart" style={{ position: "relative", color: "#fff", textDecoration: "none" }}>
            <ShoppingCart size={21} />
            {cartCount > 0 && (
              <span style={{
                position: "absolute", top: -7, right: -7,
                background: accentColor, color: "#2c1810",
                borderRadius: "50%", width: 17, height: 17,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.6rem", fontWeight: 800,
              }}>{cartCount > 9 ? "9+" : cartCount}</span>
            )}
          </Link>

          {/* User — desktop only shows name, mobile just icon */}
          {user ? (
            <div style={{ position: "relative" }}>
              <button onClick={() => setUserDropOpen(!userDropOpen)} style={{
                background: "rgba(255,255,255,0.12)", border: "none",
                borderRadius: 20, padding: "5px 10px",
                display: "flex", alignItems: "center", gap: 5,
                color: "#fff", cursor: "pointer", fontSize: "0.78rem",
              }}>
                <User size={15} />
                <span className="desktop-only" style={{ maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                  {user.email?.split("@")[0]}
                </span>
                <ChevronDown size={12} className="desktop-only" />
              </button>
              {userDropOpen && (
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 8px)",
                  background: "#fff", borderRadius: 12, minWidth: 175,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  overflow: "hidden", zIndex: 100,
                }}>
                  <Link to="/profile" onClick={() => setUserDropOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", color: "#333", textDecoration: "none", fontSize: "0.86rem", borderBottom: "1px solid #f0f0f0" }}>
                    <User size={15} /> My Profile
                  </Link>
                  <Link to="/orders" onClick={() => setUserDropOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", color: "#333", textDecoration: "none", fontSize: "0.86rem", borderBottom: "1px solid #f0f0f0" }}>
                    <Package size={15} /> My Orders
                  </Link>
                  <button onClick={handleSignOut} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", color: "#e53935", background: "none", border: "none", width: "100%", cursor: "pointer", fontSize: "0.86rem", textAlign: "left" }}>
                    <LogOut size={15} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" style={{ background: accentColor, color: "#2c1810", padding: "7px 14px", borderRadius: 20, textDecoration: "none", fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" }}>
              Login
            </Link>
          )}

          {/* Hamburger — mobile only */}
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", display: "none", padding: 2 }} className="hamburger-btn">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu ── */}
      {menuOpen && (
        <div style={{ position: "fixed", top: 64, left: 0, right: 0, zIndex: 999, background: primaryColor, padding: "12px 0 20px", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
          {[["Home", "/"], ["Shop", "/shop"], ["About", "/about"], ["Contact", "/contact"],
            ...(user ? [["My Orders", "/orders"], ["My Profile", "/profile"]] : [])
          ].map(([label, path]) => (
            <Link key={path} to={path} style={{ display: "block", padding: "13px 24px", color: "rgba(255,255,255,0.9)", textDecoration: "none", fontSize: "0.95rem", fontWeight: 700, letterSpacing: 0.5, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              {label}
            </Link>
          ))}
          <Link to="/search" style={{ display: "flex", alignItems: "center", gap: 8, padding: "13px 24px", color: "rgba(255,255,255,0.7)", textDecoration: "none", fontSize: "0.9rem", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <Search size={15} /> Search
          </Link>
          {user && (
            <button onClick={handleSignOut} style={{ display: "block", width: "100%", padding: "13px 24px", background: "none", border: "none", color: "#ff8a80", fontSize: "0.95rem", fontWeight: 700, textAlign: "left", cursor: "pointer" }}>
              Sign Out
            </button>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav  { display: none !important; }
          .desktop-only { display: none !important; }
          .hamburger-btn { display: block !important; }
        }
        @media (max-width: 360px) {
          .nav-brand-text { display: none !important; }
        }
      `}</style>
    </>
  );
}
